import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getLesson, updateLesson, generateQuestions } from '../../services/api';
import './LessonEditor.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';

const LessonEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lesson, setLesson] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    questionCount: '1',
    questionType: 'multiple-choice'
  });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    questionCount: 1,
    quizTypes: {
      'multiple-choice': false,
      'true-false': false,
      'short-answer': false,
      'essay': false
    },
    quiz: []
  });

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const loadLesson = async () => {
      try {
        setLoading(true);
        const response = await handleRequest(getLesson, id);
        const lessonData = response.data;
        
        setLesson(lessonData);
        setFormData({
          title: lessonData.title || '',
          content: lessonData.content || '',
          questionCount: lessonData.questionCount || 1,
          quizTypes: (Array.isArray(lessonData.quizTypes)
            ? lessonData.quizTypes.reduce((acc, type) => ({
                ...acc,
                [type]: true
              }), {
                'multiple-choice': false,
                'true-false': false,
                'short-answer': false,
                'essay': false
              })
            : {
                'multiple-choice': lessonData.quizTypes?.['multiple-choice'] || false,
                'true-false': lessonData.quizTypes?.['true-false'] || false,
                'short-answer': lessonData.quizTypes?.['short-answer'] || false,
                'essay': lessonData.quizTypes?.['essay'] || false
              }),
          quiz: lessonData.quiz || []
        });
        setErrorMessage('');
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn leksjonen');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [id, user, navigate, handleRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'questionCount' ? parseInt(value) || 1 : value
    }));
  };

  const handleQuizTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      quizTypes: {
        ...prev.quizTypes,
        [type]: !prev.quizTypes[type]
      }
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      quiz: prev.quiz.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      quiz: prev.quiz.map((q, i) => {
        if (i === questionIndex) {
          const newOptions = [...(q.options || [])];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      })
    }));
  };

  const addOption = (questionIndex) => {
    setFormData(prev => ({
      ...prev,
      quiz: prev.quiz.map((q, i) => {
        if (i === questionIndex) {
          return { ...q, options: [...(q.options || []), ''] };
        }
        return q;
      })
    }));
  };

  const removeOption = (questionIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      quiz: prev.quiz.map((q, i) => {
        if (i === questionIndex) {
          const newOptions = [...(q.options || [])];
          newOptions.splice(optionIndex, 1);
          return { ...q, options: newOptions };
        }
        return q;
      })
    }));
  };

  const addQuestion = () => {
    const selectedTypes = Object.keys(formData.quizTypes).filter(type => formData.quizTypes[type]);
    const defaultType = selectedTypes.length > 0 ? selectedTypes[0] : 'multiple-choice';
    
    const newQuestion = {
      type: defaultType,
      question: '',
      options: defaultType === 'multiple-choice' ? ['', ''] : (defaultType === 'true-false' ? ['True', 'False'] : []),
      correctAnswer: defaultType === 'true-false' ? 'True' : '',
      explanation: ''
    };

    setFormData(prev => ({
      ...prev,
      quiz: [...prev.quiz, newQuestion]
    }));
  };

  const removeQuestion = (index) => {
    if (window.confirm('Er du sikker på at du vil fjerne dette spørsmålet?')) {
      setFormData(prev => ({
        ...prev,
        quiz: prev.quiz.filter((_, i) => i !== index)
      }));
    }
  };

  const handleGenerateQuestions = async () => {
    try {
      setGenerating(true);
      setErrorMessage('');

      if (!formData.content || !formData.content.trim()) {
        setErrorMessage('Leksjonsinnhold er påkrevd for å generere spørsmål');
        setGenerating(false);
        return;
      }

      const questionCount = parseInt(generateForm.questionCount);
      if (!questionCount || questionCount < 1 || questionCount > 20) {
        setErrorMessage('Antall spørsmål må være mellom 1 og 20');
        setGenerating(false);
        return;
      }

      const response = await handleRequest(
        generateQuestions, 
        id, 
        {
          questionCount: questionCount,
          questionType: generateForm.questionType
        }
      );

      const generatedQuestions = response.data;
      
      // Add generated questions to existing quiz
      setFormData(prev => ({
        ...prev,
        quiz: [...prev.quiz, ...generatedQuestions]
      }));

      setShowGenerateModal(false);
      setGenerateForm({
        questionCount: '1',
        questionType: 'multiple-choice'
      });
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere spørsmål');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMessage('');

      const selectedQuizTypes = Object.keys(formData.quizTypes).filter(type => formData.quizTypes[type]);
      
      if (selectedQuizTypes.length === 0) {
        setErrorMessage('Velg minst én quiztype');
        setSaving(false);
        return;
      }

      if (formData.quiz.length === 0) {
        setErrorMessage('Legg til minst ett spørsmål');
        setSaving(false);
        return;
      }

      // Validate all questions
      for (let i = 0; i < formData.quiz.length; i++) {
        const q = formData.quiz[i];
        if (!q.question || !q.question.trim()) {
          setErrorMessage(`Spørsmål ${i + 1}: Spørsmålstekst er påkrevd`);
          setSaving(false);
          return;
        }
        if (!q.correctAnswer || !q.correctAnswer.trim()) {
          setErrorMessage(`Spørsmål ${i + 1}: Riktig svar er påkrevd`);
          setSaving(false);
          return;
        }
        if ((q.type === 'multiple-choice' || q.type === 'true-false') && (!q.options || q.options.length < 2)) {
          setErrorMessage(`Spørsmål ${i + 1}: Minst 2 alternativer er påkrevd`);
          setSaving(false);
          return;
        }
      }

      const formattedData = {
        title: formData.title,
        content: formData.content,
        questionCount: formData.quiz.length,
        quizTypes: selectedQuizTypes,
        quiz: formData.quiz.map(q => ({
          type: q.type,
          question: q.question.trim(),
          options: q.options ? q.options.map(opt => opt.trim()).filter(opt => opt) : [],
          correctAnswer: q.correctAnswer.trim(),
          explanation: q.explanation ? q.explanation.trim() : ''
        }))
      };

      const response = await handleRequest(updateLesson, id, formattedData);
      
      // Verify the quiz was saved correctly
      if (response.data && response.data.quiz) {
        console.log('Quiz saved successfully with', response.data.quiz.length, 'questions');
      }
      
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke oppdatere leksjonen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="lesson-editor-container">
        <div className="loading">Laster leksjon...</div>
      </div>
    );
  }

  return (
    <div className="lesson-editor-container">
      <div className="lesson-editor-header">
        <h1>Rediger leksjon</h1>
        <button 
          className="back-btn"
          onClick={() => navigate('/dashboard')}
        >
          ← Tilbake til oversikten
        </button>
      </div>

      {errorMessage && <ErrorCard message={errorMessage} />}

      <form onSubmit={handleSubmit} className="lesson-editor-form">
        <div className="form-section">
          <h2>Leksjonsinformasjon</h2>
          
          <div className="form-group">
            <label htmlFor="title">Leksjonstittel</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Skriv inn leksjonstittel"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Leksjonsinnhold</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Skriv inn leksjonsinnhold"
              rows="6"
              required
            />
          </div>

          <div className="form-group">
            <label>Quiztyper</label>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="multiple-choice"
                  checked={formData.quizTypes['multiple-choice']}
                  onChange={() => handleQuizTypeChange('multiple-choice')}
                />
                <label htmlFor="multiple-choice">Flervalg</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="true-false"
                  checked={formData.quizTypes['true-false']}
                  onChange={() => handleQuizTypeChange('true-false')}
                />
                <label htmlFor="true-false">Sant/usant</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="short-answer"
                  checked={formData.quizTypes['short-answer']}
                  onChange={() => handleQuizTypeChange('short-answer')}
                />
                <label htmlFor="short-answer">Kortsvar</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="essay"
                  checked={formData.quizTypes['essay']}
                  onChange={() => handleQuizTypeChange('essay')}
                />
                <label htmlFor="essay">Essay</label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Spørsmål ({formData.quiz.length})</h2>
            <button 
              type="button"
              className="generate-questions-btn"
              onClick={() => setShowGenerateModal(true)}
            >
              Generer spørsmål
            </button>
          </div>

          {formData.quiz.map((question, qIndex) => (
            <div key={qIndex} className="question-card">
              <div className="question-header">
                <h3>Spørsmål {qIndex + 1}</h3>
                <button
                  type="button"
                  className="remove-question-btn"
                  onClick={() => removeQuestion(qIndex)}
                  title="Fjern spørsmål"
                >
                  ×
                </button>
              </div>

              <div className="form-group">
                <label>Spørsmålstype</label>
                <select
                  value={question.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    
                    // Update the question with new type and adjust options/answer accordingly
                    setFormData(prev => ({
                      ...prev,
                      quiz: prev.quiz.map((q, i) => {
                        if (i === qIndex) {
                          let updatedQuestion = { ...q, type: newType };
                          
                          // Auto-set options for true/false
                          if (newType === 'true-false') {
                            updatedQuestion.options = ['True', 'False'];
                            if (!updatedQuestion.correctAnswer || !['True', 'False'].includes(updatedQuestion.correctAnswer)) {
                              updatedQuestion.correctAnswer = 'True';
                            }
                          } else if (newType === 'multiple-choice') {
                            // Ensure at least 2 options for multiple choice
                            if (!updatedQuestion.options || updatedQuestion.options.length < 2) {
                              updatedQuestion.options = ['', ''];
                            }
                          } else {
                            // Clear options for short-answer and essay
                            updatedQuestion.options = [];
                          }
                          
                          return updatedQuestion;
                        }
                        return q;
                      })
                    }));
                  }}
                >
                  <option value="multiple-choice">Flervalg</option>
                  <option value="true-false">Sant/usant</option>
                  <option value="short-answer">Kortsvar</option>
                  <option value="essay">Essay</option>
                </select>
              </div>

              <div className="form-group">
                <label>Spørsmålstekst</label>
                <textarea
                  value={question.question}
                  onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                  placeholder="Skriv inn spørsmålet"
                  rows="3"
                  required
                />
              </div>

              {question.type === 'multiple-choice' && (
                <div className="form-group">
                  <label>Alternativer</label>
                  {(question.options || []).map((option, optIndex) => (
                    <div key={optIndex} className="option-row">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                        placeholder={`Alternativ ${optIndex + 1}`}
                        required
                      />
                      <button
                        type="button"
                        className="remove-option-btn"
                        onClick={() => removeOption(qIndex, optIndex)}
                        disabled={(question.options || []).length <= 2}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="add-option-btn"
                    onClick={() => addOption(qIndex)}
                  >
                    + Legg til alternativ
                  </button>
                </div>
              )}
              
              {question.type === 'true-false' && (
                <div className="form-group">
                  <label>Alternativer</label>
                  <div className="true-false-options">
                    <div className="option-row">
                      <input type="text" value="True" disabled />
                    </div>
                    <div className="option-row">
                      <input type="text" value="False" disabled />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Riktig svar</label>
                {question.type === 'multiple-choice' || question.type === 'true-false' ? (
                  <select
                    value={question.correctAnswer}
                    onChange={(e) => handleQuestionChange(qIndex, 'correctAnswer', e.target.value)}
                    required
                  >
                    <option value="">Velg riktig svar</option>
                    {(question.options || []).map((option, optIndex) => (
                      <option key={optIndex} value={option}>
                        {option || `Alternativ ${optIndex + 1}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={question.correctAnswer}
                    onChange={(e) => handleQuestionChange(qIndex, 'correctAnswer', e.target.value)}
                    placeholder="Skriv inn riktig svar"
                    required
                  />
                )}
              </div>

              <div className="form-group">
                <label>Forklaring (valgfritt)</label>
                <textarea
                  value={question.explanation || ''}
                  onChange={(e) => handleQuestionChange(qIndex, 'explanation', e.target.value)}
                  placeholder="Skriv inn forklaring til svaret"
                  rows="2"
                />
              </div>
            </div>
          ))}

          {formData.quiz.length === 0 && (
            <div className="no-questions">
              <p>Ingen spørsmål ennå. Trykk «Legg til spørsmål» eller «Generer spørsmål» for å komme i gang.</p>
            </div>
          )}

          <div className="question-actions-footer">
            <button 
              type="button"
              className="add-question-btn"
              onClick={addQuestion}
            >
              + Legg til spørsmål
            </button>
          </div>
        </div>

        {showGenerateModal && (
          <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Generer spørsmål</h3>
                <button 
                  className="modal-close-btn"
                  onClick={() => setShowGenerateModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="generate-count">Antall spørsmål</label>
                  <input
                    type="number"
                    id="generate-count"
                    min="1"
                    max="20"
                    value={generateForm.questionCount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string while typing, but validate on blur
                      if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 20)) {
                        setGenerateForm(prev => ({
                          ...prev,
                          questionCount: value
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure valid value on blur
                      const value = parseInt(e.target.value);
                      if (!value || value < 1) {
                        setGenerateForm(prev => ({
                          ...prev,
                          questionCount: '1'
                        }));
                      } else if (value > 20) {
                        setGenerateForm(prev => ({
                          ...prev,
                          questionCount: '20'
                        }));
                      } else {
                        setGenerateForm(prev => ({
                          ...prev,
                          questionCount: value.toString()
                        }));
                      }
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="generate-type">Spørsmålstype</label>
                  <select
                    id="generate-type"
                    value={generateForm.questionType}
                    onChange={(e) => setGenerateForm(prev => ({
                      ...prev,
                      questionType: e.target.value
                    }))}
                  >
                    <option value="multiple-choice">Flervalg</option>
                    <option value="true-false">Sant/usant</option>
                    <option value="short-answer">Kortsvar</option>
                    <option value="essay">Essay</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowGenerateModal(false)}
                >
                  Avbryt
                </button>
                <button 
                  className="generate-btn"
                  onClick={handleGenerateQuestions}
                  disabled={generating}
                >
                  {generating ? 'Genererer...' : 'Generer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button 
            type="button"
            className="cancel-btn"
            onClick={() => navigate('/dashboard')}
          >
            Avbryt
          </button>
          <button 
            type="submit"
            className="save-btn"
            disabled={saving}
          >
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LessonEditor;
